/* Phase 5 · Job Work Challan detail — record returns, short-close, print PDF */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function JobWorkChallanDetail() {
  const { cid } = useParams();
  const [c, setC] = useState(null);
  const [returns, setReturns] = useState({});          // line_id -> {qty, condition, note}
  const [showShortClose, setShowShortClose] = useState(false);
  const [scReason, setScReason] = useState("");

  const load = () => api.get(`/job-work/challans/${cid}`).then(r => setC(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  if (!c) return <div className="p-6 text-sm text-[#7C8E9E]">Loading…</div>;

  const submitReturn = async () => {
    const lines = Object.entries(returns)
      .filter(([, v]) => v && parseFloat(v.qty) > 0)
      .map(([line_id, v]) => ({ line_id, returned_qty: parseFloat(v.qty), condition: v.condition || "ok", note: v.note || "" }));
    if (lines.length === 0) return toast.error("Enter at least one return quantity");
    try {
      const r = await api.post(`/job-work/challans/${cid}/returns`, { lines });
      toast.success(`Return recorded. ${r.data.fixing_records_created || 0} fixing record(s) created.`);
      setReturns({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Return failed");
    }
  };

  const shortClose = async () => {
    if (!scReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/job-work/challans/${cid}/short-close`, { reason: scReason });
      toast.success("Short-closed");
      setShowShortClose(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Short-close failed");
    }
  };

  const printPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    // Header
    doc.setFontSize(16).setFont("helvetica", "bold");
    doc.text("HAG Carpets Pvt. Ltd.", 14, 18);
    doc.setFontSize(9).setFont("helvetica", "normal");
    doc.text("Mirzapur · UP · India · GSTIN: 09AABCH1234A1Z5", 14, 24);
    doc.setFontSize(11).setFont("helvetica", "bold");
    doc.text("DELIVERY CHALLAN (JOB WORK) — NOT FOR SALE", 14, 34);
    doc.setFontSize(9).setFont("helvetica", "normal");
    doc.text(`GST §143 · Rule 45 · Challan No: ${c.challan_no}`, 14, 40);
    doc.text(`Date: ${c.date?.slice(0, 10)}   Purpose: ${c.purpose}`, 14, 46);
    doc.text(`Expected return: ${c.expected_return_date}   Vehicle: ${c.vehicle_no || "-"}`, 14, 52);

    doc.setFont("helvetica", "bold").text("Consignor:", 14, 62);
    doc.setFont("helvetica", "normal").text("HAG Carpets Pvt Ltd, Mirzapur — GSTIN 09AABCH1234A1Z5", 40, 62);
    doc.setFont("helvetica", "bold").text("Consignee:", 14, 68);
    doc.setFont("helvetica", "normal").text(`${c.job_worker_name} — GSTIN ${c.job_worker_gstin || "UNREGISTERED"}`, 40, 68);

    autoTable(doc, {
      startY: 76,
      head: [["#", "Description", "HSN", "Qty", "Approx Value (₹)"]],
      body: c.lines.map((l, i) => [
        i + 1,
        (l.kind === "piece" ? `[${l.piece_code || "PIECE"}] ` : "") + (l.description || ""),
        l.hsn || "5701",
        l.qty,
        l.approx_value?.toFixed?.(2) || l.approx_value,
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [11, 35, 64] },
    });

    const y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8).setFont("helvetica", "italic");
    doc.text("Goods sent for job work; not a sale. Return within 12 months per GST §143.", 14, y);
    doc.text(`Issued by: ${c.issued_by_name || "-"}`, 14, y + 6);
    doc.line(140, y + 20, 190, y + 20);
    doc.text("Authorised Signatory", 148, y + 24);

    doc.save(`${c.challan_no}.pdf`);
  };

  const ageStyle = { green: "bg-emerald-100 text-emerald-800", amber: "bg-amber-100 text-amber-800", red: "bg-red-100 text-red-800" };

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="jw-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">
            {c.challan_no} · <span className={`px-2 py-0.5 rounded-full ${ageStyle[c.ageing_flag] || ""}`}>{c.ageing_flag}</span>
          </div>
          <h1 className="text-2xl font-bold text-[#0B2340]">
            {c.job_worker_name} · {c.purpose}
          </h1>
          <div className="text-xs text-[#7C8E9E]">
            GSTIN: {c.job_worker_gstin || "UNREGISTERED"} · Return by {c.expected_return_date} · Status {c.status}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={printPDF} data-testid="jw-pdf"
            className="px-3 py-2 rounded-full bg-[#0B2340] text-white text-xs">
            Print PDF
          </button>
          {c.status === "Open" && (
            <button onClick={() => setShowShortClose(true)} data-testid="jw-short-close"
              className="px-3 py-2 rounded-full bg-white border border-red-200 text-red-600 text-xs">
              Short-close
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 overflow-x-auto mb-4">
        <table className="min-w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-[#7C8E9E]">
            <tr className="border-b border-slate-100">
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Description / Piece</th>
              <th className="text-right p-3">Qty</th>
              <th className="text-right p-3">Returned</th>
              <th className="text-right p-3">Balance</th>
              <th className="text-left p-3">Status</th>
              {c.status === "Open" && <th className="p-3">Record return</th>}
            </tr>
          </thead>
          <tbody>
            {c.lines.map((l, idx) => {
              const bal = l.qty - l.returned_qty;
              const r = returns[l.line_id] || {};
              return (
                <tr key={l.line_id} className="border-b border-slate-50" data-testid={`jw-line-${idx}`}>
                  <td className="p-3">{idx + 1}</td>
                  <td className="p-3 text-xs uppercase">{l.kind}</td>
                  <td className="p-3">
                    {l.kind === "piece" ? <span className="font-mono text-[11px]">{l.piece_code}</span> : null} {l.description}
                  </td>
                  <td className="p-3 text-right">{l.qty}</td>
                  <td className="p-3 text-right">{l.returned_qty}</td>
                  <td className="p-3 text-right font-medium">{bal}</td>
                  <td className="p-3 text-xs">{l.status}</td>
                  {c.status === "Open" && (
                    <td className="p-3">
                      {l.status !== "Closed" && bal > 0 ? (
                        <div className="flex flex-wrap gap-1 items-center">
                          <input type="number" min="0" step="0.01" max={bal} placeholder={String(bal)}
                            data-testid={`jw-line-${idx}-qty`}
                            className="border rounded px-2 py-1 text-xs w-16"
                            value={r.qty || ""}
                            onChange={e => setReturns({ ...returns, [l.line_id]: { ...r, qty: e.target.value } })} />
                          {l.kind === "piece" && (
                            <select className="border rounded px-1 py-1 text-xs"
                              data-testid={`jw-line-${idx}-cond`}
                              value={r.condition || "ok"}
                              onChange={e => setReturns({ ...returns, [l.line_id]: { ...r, condition: e.target.value } })}>
                              <option value="ok">OK</option>
                              <option value="issue">Issue → fixing</option>
                            </select>
                          )}
                          <input placeholder="note"
                            className="border rounded px-2 py-1 text-xs flex-1 min-w-[80px]"
                            value={r.note || ""}
                            onChange={e => setReturns({ ...returns, [l.line_id]: { ...r, note: e.target.value } })} />
                        </div>
                      ) : (
                        <span className="text-xs text-[#7C8E9E]">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {c.status === "Open" && (
        <button onClick={submitReturn} data-testid="jw-submit-return"
          className="px-5 py-2 rounded-full bg-[#B08B49] text-white text-sm hover:bg-[#95753d]">
          Record return
        </button>
      )}

      {showShortClose && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4" data-testid="jw-sc-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="font-bold text-lg mb-3">Short-close challan</h3>
            <p className="text-xs text-[#7C8E9E] mb-3">Any pieces still at the job worker will be marked written-off. This action is audit-logged.</p>
            <textarea rows="3" placeholder="Reason (required)"
              className="w-full border rounded px-2 py-1 text-sm mb-3"
              data-testid="jw-sc-reason"
              value={scReason} onChange={e => setScReason(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowShortClose(false)} className="px-3 py-1 text-xs">Cancel</button>
              <button onClick={shortClose} data-testid="jw-sc-confirm"
                className="px-3 py-1 rounded-full bg-red-600 text-white text-xs">Confirm short-close</button>
            </div>
          </div>
        </div>
      )}

      {c.short_closed_reason && (
        <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
          Short-closed: {c.short_closed_reason}
        </div>
      )}
    </div>
  );
}
